import Toggle from '@clientShared/toggle';
import { getCookieByName, setCookie, supportsNativePopover } from '@clientShared/utils';
import { GetLoginFlyout, GetLoginSuggestionPopover } from '@clientWebServices/header.service';
import MessageService from '@clientWebServices/message.service';
import { globalEvent } from '@shared/easyfy.core';
import { invoke } from '@shared/easyfy.decorators';
import { addCss, removeCss } from '@shared/easyfy.utils';

const TOGGLE_LOGIN_FLYOUT_ACTION = 'toggle-login-flyout';
const HEADER_LOGIN_BUTTON_SELECTOR = '[data-container="header-login-button"]';
const LOGIN_SUGGESTION_POPOVER_SELECTOR = '[data-container="login-suggestion-popover"]';
const LOGIN_SUGGESTION_POPOVER_CLOSE_BUTTON_SELECTOR = '[data-container="close-button"]';
const LOGIN_SUGGESTION_COOKIE_NAME = 'hasOpenedLoginSuggestionPopover';

@invoke
export default class MyPagesButton {
  private isLoginFlyoutLoaded: boolean = false;
  private hasHandledLoginRequest: boolean = false;
  private loginSuggestionPopoverHtml: string | undefined;
  onInit(): void {
    globalEvent({
      type: 'click',
      events: [
        {
          action: TOGGLE_LOGIN_FLYOUT_ACTION,
          callback: this.toggleLoginFlyout,
        },
      ],
    });

    document.addEventListener('readystatechange', () => {
      this.openLoginFlyoutForLoginRequest();
    });

    this.setupLoginButtonInteractions();
  }

  private setupLoginButtonInteractions = async (forceLoginHandler: boolean = false): Promise<void> => {
    const loginButtons: NodeListOf<HTMLButtonElement> = document.querySelectorAll(HEADER_LOGIN_BUTTON_SELECTOR);
    const loginSuggestionButtons: HTMLButtonElement[] = [];

    loginButtons?.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      const isAuthenticated = (button.dataset.authenticated ?? '').toLowerCase() === 'true';

      if (isAuthenticated || getCookieByName(LOGIN_SUGGESTION_COOKIE_NAME) || forceLoginHandler) {
        this.assignLoginFlyoutHandler(button);
      } else {
        loginSuggestionButtons.push(button);
      }
    });

    if (loginSuggestionButtons.length > 0) {
      await this.loadLoginSuggestionPopover();
      loginSuggestionButtons.forEach((button) => this.setupLoginSuggestionPopover(button));
    }
  };

  private loadLoginSuggestionPopover = async (): Promise<void> => {
    try {
      const response = await GetLoginSuggestionPopover();
      const partialHtml = response?.data?.partialHtml;
      if (!partialHtml) throw new Error('Missing login suggestion popover partial html.');
      this.loginSuggestionPopoverHtml = partialHtml;
    } catch {
      this.setupLoginButtonInteractions(true);
    }
  };

  private setupLoginSuggestionPopover = (button: HTMLButtonElement): void => {
    const popover = button.parentElement?.querySelector<HTMLElement>(LOGIN_SUGGESTION_POPOVER_SELECTOR);
    if (!popover) return;

    this.showNotificationBadge(button);

    if (supportsNativePopover()) {
      this.setupNativePopover(button, popover);
    } else {
      this.setupFallbackPopover(button, popover);
    }
  };

  private showNotificationBadge = (button: HTMLButtonElement): void => {
    const badge = button.querySelector<HTMLElement>('.badge');
    if (badge instanceof HTMLElement) removeCss(badge, 'hidden');
  };

  private hideNotificationBadge = (button: HTMLButtonElement): void => {
    const badge = button.querySelector<HTMLElement>('.badge');
    if (badge instanceof HTMLElement) addCss(badge, 'hidden');
  };

  private setupNativePopover = (button: HTMLButtonElement, popover: HTMLElement): void => {
    const id = crypto.randomUUID();

    popover.id = id;

    button.setAttribute('popovertarget', id);
    button.setAttribute('popoverTargetAction', 'toggle');
    this.addPopoverEvents(button, popover);
  };

  private addPopoverEvents = (button: HTMLButtonElement, popover: HTMLElement): void => {
    popover.addEventListener('toggle', (event) => {
      if (!(event instanceof ToggleEvent)) return;

      if (event.newState === 'open') {
        popover.innerHTML = this.loginSuggestionPopoverHtml ?? '';
        const closeButton = popover.querySelector<HTMLButtonElement>(LOGIN_SUGGESTION_POPOVER_CLOSE_BUTTON_SELECTOR);
        closeButton?.setAttribute('popoverTarget', popover.id);
        closeButton?.setAttribute('popoverTargetAction', 'toggle');

        this.hideNotificationBadge(button);
        this.setPopoverOpenedCookie();
      }
      if (event.newState === 'closed') {
        this.assignLoginFlyoutHandler(button);
      }
    });
  };

  private setupFallbackPopover(button: HTMLButtonElement, popover: HTMLElement): void {
    popover.removeAttribute('popover');
    addCss(popover, 'fallback');
    button.addEventListener('click', this.showPopoverFallback);
  }

  private showPopoverFallback = (e: MouseEvent): void => {
    const clickedElement: HTMLButtonElement = e.currentTarget as HTMLButtonElement;
    const popover = clickedElement?.parentElement?.querySelector<HTMLElement>(LOGIN_SUGGESTION_POPOVER_SELECTOR);

    if (!popover) return;
    popover.innerHTML = this.loginSuggestionPopoverHtml ?? '';
    addCss(popover, 'active');
    this.hideNotificationBadge(clickedElement);
    this.setPopoverOpenedCookie();

    setTimeout(() => {
      window.addEventListener('click', this.hidePopoverFallback);
    }, 0);
  };

  private hidePopoverFallback = (e?: MouseEvent): void => {
    const clickedElement = e?.target as HTMLElement;
    const popover = document.querySelector<HTMLElement>(`${LOGIN_SUGGESTION_POPOVER_SELECTOR}.active`);
    const closeButton = popover?.querySelector<HTMLButtonElement>(LOGIN_SUGGESTION_POPOVER_CLOSE_BUTTON_SELECTOR);
    const button = popover?.parentElement?.querySelector<HTMLButtonElement>(HEADER_LOGIN_BUTTON_SELECTOR);

    if (!popover || !button) return;

    const shouldClose =
      !e || !popover.contains(clickedElement) || clickedElement === closeButton || clickedElement.dataset.action === TOGGLE_LOGIN_FLYOUT_ACTION;

    if (shouldClose) {
      removeCss(popover, 'active');
      this.assignLoginFlyoutHandler(button);

      setTimeout(() => {
        window.removeEventListener('click', this.hidePopoverFallback);
        button.removeEventListener('click', this.showPopoverFallback);
      }, 0);
    }
  };

  toggleLoginFlyout = async (e?: MouseEvent): Promise<void> => {
    e?.preventDefault();
    e?.stopPropagation();

    let target = e?.target;

    if (!e?.target) {
      target = document.querySelector(`[data-action="${TOGGLE_LOGIN_FLYOUT_ACTION}"]`);
    }

    if (!target || !(target instanceof HTMLButtonElement)) return;

    const redirectUrl = target.dataset?.redirectUrl;

    Toggle.Instance.closeOpenModals();
    this.closeOpenPopovers();

    await GetLoginFlyout()
      .then((response) => {
        if (response.data?.partialHtml) {
          document.body.insertAdjacentHTML('beforeend', response.data.partialHtml);
          addCss(document.body, 'noof');
          addCss(document.body, 'overlay-is-active');
          this.initializeLoginFlyoutToggles();
          this.isLoginFlyoutLoaded = true;

          if (redirectUrl) {
            this.addRedirectUrlToLoginForm(redirectUrl);
          }
        }
      })
      .catch((error) => {
        console.log(error);
        MessageService.Instance.displayDefaultErrorMessage();
      });

    Toggle.Instance.onToggleElement(e ?? null, target, false);
  };

  private addRedirectUrlToLoginForm = (redirectUrl: string): void => {
    const formElement = document.querySelector<HTMLFormElement>('[data-container="login-form"]');
    if (formElement) {
      formElement.dataset.redirectUrl = redirectUrl;
    }
  };

  private assignLoginFlyoutHandler = (button: HTMLButtonElement): void => {
    if (this.isLoginFlyoutLoaded) {
      this.configureAsToggleButton(button);
    } else {
      this.configureAsActionButton(button);
    }
  };

  private initializeLoginFlyoutToggles = (): void => {
    const buttons: NodeListOf<HTMLButtonElement> = document.querySelectorAll(`[data-action="${TOGGLE_LOGIN_FLYOUT_ACTION}"]`);

    buttons?.forEach((button) => {
      this.configureAsToggleButton(button);
    });
  };

  private configureAsToggleButton = (button: HTMLButtonElement): void => {
    button.removeAttribute('data-action');
    button.dataset.event = 'toggle';
    button.dataset.overlay = '';
    button.dataset.target = 'login-flyout';
  };

  private configureAsActionButton = (button: HTMLButtonElement): void => {
    button.removeAttribute('data-event');
    button.dataset.action = TOGGLE_LOGIN_FLYOUT_ACTION;
  };

  private closeOpenPopovers = (): void => {
    if (supportsNativePopover()) {
      Toggle.Instance.closeOpenPopovers();
    } else {
      const openPopover = document.querySelector<HTMLElement>(`${LOGIN_SUGGESTION_POPOVER_SELECTOR}.active`);
      if (!openPopover) return;
      this.hidePopoverFallback();
    }
  };

  private setPopoverOpenedCookie = (): void => {
    const date = new Date();
    date.setDate(date.getDate() + 30); // Add 30 days

    setCookie('true', LOGIN_SUGGESTION_COOKIE_NAME, date, 'Strict');
  };

  openLoginFlyoutForLoginRequest = (): void => {
    // readystatechange fires twice; without the guard the second firing toggled the flyout again.
    if (this.hasHandledLoginRequest || !this.isLoginRequest()) return;

    // No button yet? Leave the flag unset so the next readystatechange retries.
    const target = document.querySelector(`[data-action="${TOGGLE_LOGIN_FLYOUT_ACTION}"]`);
    if (!(target instanceof HTMLButtonElement)) return;

    this.hasHandledLoginRequest = true;
    this.toggleLoginFlyout();
  };

  isLoginRequest = (): boolean => {
    if (window.location.href.indexOf('#login') > -1) {
      return true;
    }

    // Check if URL has showLogin=true query parameter (set by server redirect)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('showLogin') === 'true' && !window.location.hash) {
      window.location.hash = 'login';
      return true;
    }

    return false;
  };
}
